import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { sessionService } from "redux-react-session";
import { mixed, object } from "yup";
import { useFormik } from "formik";
import { Box, List, ListItem } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";

import { useSession } from "../../hooks/useSession";
import {
  authOptionsFooterDivider,
  callAPIInterface,
  concatenatedMobileNumber,
  getCountryObj,
  handleContactSupport,
  mobileNumberWithoutCallingCode,
  validatePhoneNumber,
} from "../constants";
import BackdropLoader from "../backdropLoader/BackdropLoader";
import {
  authAppSteps,
  authenticatorMsg,
  carrierRateMsg,
  contactSupport,
  gAuthGuideline,
  loadingMsg,
  mobile,
  next,
  retry,
  sendCode,
  setupAuthenticatorApp,
  smsAuthentication,
  twoFactorAuthenticationSubHeader,
  twoStepVerification,
  useAnotherMethod,
  useAnotherWay,
} from "../messages";
import Text from "../text/Text";
import PhoneNumberInput from "../phoneNumberInput/PhoneNumberInput";
import { countries } from "../country";
import Button from "../button/Button";
import CommonHeader from "../CommonHeader";
import CommonAuthHeader from "../options/CommonAuthHeader";
import { CustomLink } from "../link/Link";
import CommonSkeleton from "../CommonSkeleton";
import Clipboard from "../clipboard/Clipboard";
import SelectAuthenticationMethodDrawer from "./SelectAuthenticationMethodDrawer";

const validationSchema = object({
  mobileNo: mixed().test("mobileNo", validatePhoneNumber).required(""),
});

const Setup2FA = ({ isFromOptions = false }) => {
  const { session, error } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  const [countryISOCode, setCountryISOCode] = useState();
  const [loginMfaAuthDetails, setLoginMfaAuthDetails] = useState([]);
  const [isBtnDisabled, setIsBtnDisabled] = useState(true);
  const [backdropLoading, setBackdropLoading] = useState(false);
  const [gAuthSetup, setGAuthSetup] = useState(null);
  const [gAuthError, setGAuthError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openSelectMethodModal, setOpenSelectMethodModal] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState("google_authenticator");

  const locationState = location?.state;
  const isFromLoginPage = locationState?.from === "/login";
  const isFromTwoFactor = locationState?.from === "/two-factor";
  const setupMethod = locationState?.mfa_type || "google_authenticator";
  const isFromVerifyEmailPage = locationState?.isFromVerifyEmailPage;
  const qrSize = isFromOptions ? 254 : 210;

  useEffect(() => {
    if (!isFromLoginPage && !isFromTwoFactor) {
      navigate(-1);
    }
  }, []);

  useEffect(() => {
    if (session) {
      const isGoogleAuth = setupMethod === "google_authenticator";
      isGoogleAuth && getGAuthConfigurations();
      setSelectedMethod(setupMethod);

      if (session.mfa_configurations) {
        setLoginMfaAuthDetails(session.mfa_configurations);
      }
    }

    if (error) {
      navigate(-1);
    }
  }, [session, error]);

  const getGAuthConfigurations = async () => {
    setIsLoading(true);
    try {
      const mfaApiPath = "/login/mfa/google-auth";

      const apiData = { session: session?.session };
      const res = await callAPIInterface("POST", mfaApiPath, apiData);

      await sessionService.saveSession({
        ...session,
        mfa_configurations: [res],
      });

      setGAuthSetup(res);
      setGAuthError(false);
    } catch (_err) {
      setGAuthError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitPhoneNumber = async (val) => {
    try {
      setBackdropLoading(true);

      const phoneNumData = {
        session: session?.session,
        mfa_calling_code: val.mobileDialCode,
        mfa_phone_number: mobileNumberWithoutCallingCode(
          val.mobileDialCode,
          val.mobileNo
        ),
      };

      const response = await callAPIInterface(
        "POST",
        "/login/mfa/phone-number",
        phoneNumData
      );

      await sessionService.saveSession({
        ...session,
        otp_expires_at: response.otp_expires_at,
        mfa_configurations: [response],
      });

      navigate("/two-factor", {
        state: {
          from: "/two-factor-setup",
          isFromVerifyEmailPage,
        },
      });
    } catch (error) {
      console.error("Error submitting phone number", error);
    } finally {
      setBackdropLoading(false);
    }
  };

  const smsConfigDetails = useMemo(() => {
    return (
      loginMfaAuthDetails?.length &&
      loginMfaAuthDetails.filter(
        (item) => item.mfa_type.toLowerCase() === "sms"
      )?.[0]
    );
  }, [loginMfaAuthDetails]);

  const mobileInputDetail = useMemo(() => {
    return (
      smsConfigDetails && {
        callingCode: smsConfigDetails.mfa_calling_code,
        mobileNumber: concatenatedMobileNumber(
          smsConfigDetails.mfa_calling_code,
          smsConfigDetails.mfa_phone_number
        ),
      }
    );
  }, [smsConfigDetails]);

  const formik = useFormik({
    initialValues: {
      mobileDialCode: "1",
      mobileNo: "",
    },
    validationSchema: validationSchema,
    onSubmit: handleSubmitPhoneNumber,
    enableReinitialize: true,
  });

  const {
    values,
    setFieldValue,
    setTouched,
    isValid,
    dirty,
    isSubmitting,
    errors,
    touched,
    handleSubmit,
  } = formik;

  useEffect(() => {
    setIsBtnDisabled(
      smsConfigDetails && mobileInputDetail.mobileNumber && isValid
        ? false
        : !(isValid && dirty) || isSubmitting
    );
  }, [isValid, dirty, isSubmitting, smsConfigDetails]);

  useEffect(() => {
    // Set country code according to calling_code
    let countryObj = getCountryObj("calling_code", values?.mobileDialCode);
    setCountryISOCode(countryObj?.iso2);
  }, [values?.mobileDialCode]);

  const handleDynamicWidthForMenu = async () => {
    document.body.style.overflow = "hidden";
  };

  return (
    <>
      <BackdropLoader
        open={backdropLoading}
        alt={loadingMsg}
        text={loadingMsg}
        customClass="loading-in-auth"
      />
      <CommonHeader
        icon="backArrow"
        textAfterIcon={
          selectedMethod === "google_authenticator"
            ? setupAuthenticatorApp
            : smsAuthentication
        }
      />
      <CommonAuthHeader showSpeedLogo={false} sx={{ height: "100%" }}>
        {selectedMethod === "google_authenticator" ? (
          <Box className="gauth-container">
            <Box className="gauth-wrapper">
              <Text size={14} font="regular">
                {authenticatorMsg}
              </Text>
              <Box className="gauth-modal-container">
                <List
                  sx={{
                    listStyle: "decimal",
                    pl: 4,
                    "& li::marker": {
                      fontSize: "14px",
                    },
                  }}
                >
                  {authAppSteps.map((step, idx) => (
                    <ListItem key={idx} sx={{ display: "list-item" }}>
                      <Text size={14}>{step}</Text>
                    </ListItem>
                  ))}
                </List>
                {isLoading ? (
                  <>
                    <CommonSkeleton
                      variant="rectangular"
                      height={qrSize}
                      width={qrSize}
                      sx={{
                        borderRadius: "10px",
                      }}
                    />
                    <CommonSkeleton
                      width="100%"
                      height={30}
                      sx={{ marginTop: "20px" }}
                    />
                  </>
                ) : gAuthError ? (
                  <Box height={qrSize} width={qrSize} display="flex">
                    <CustomLink
                      bold
                      sx={{ margin: "auto" }}
                      alignIcon="start"
                      withIcon={<ReplayIcon />}
                      onClick={() => getGAuthConfigurations()}
                    >
                      {retry.toUpperCase()}
                    </CustomLink>
                  </Box>
                ) : (
                  <>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <img
                        src={gAuthSetup?.otp_auth_url}
                        height={qrSize}
                        width={qrSize}
                        alt="qr-code"
                        className="google-auth-qr-img"
                      />
                      {/* <Input
                      fullWidth
                      value={gAuthSetup?.secret_key}
                      type="text"
                      customClass="code-field"
                      disabled
                      color="#2A67FF"
                      endAdornment={
                        <Clipboard
                          text={gAuthSetup?.secret_key}
                          height="18px"
                          width="18px"
                        />
                      }
                    /> */}
                    </Box>
                    <Box className="google-auth-code-box">
                      <Box className="code-box-label">
                        <Text className="scroll-text">
                          {gAuthSetup?.secret_key}
                        </Text>
                      </Box>
                      <Clipboard
                        text={gAuthSetup?.secret_key}
                        height="18px"
                        width="18px"
                      />
                    </Box>
                  </>
                )}
              </Box>
              <Text className="grey-text mb-10" size={12} font="regular">
                {gAuthGuideline}
              </Text>
            </Box>
            {gAuthSetup && (
              <Box className="gauth-footer">
                {isFromOptions && authOptionsFooterDivider}
                <Button
                  fullWidth
                  label={next}
                  onClick={() => {
                    navigate("/two-factor", {
                      state: {
                        from: "/two-factor-setup",
                        isFromVerifyEmailPage,
                      },
                    });
                  }}
                  variant="contained"
                  color="primary"
                />
              </Box>
            )}

            {gAuthError && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <Button
                  fullWidth
                  label={useAnotherWay}
                  onClick={() => {
                    setSelectedMethod("sms");
                  }}
                  sx={{ mb: "10px" }}
                  variant="outlined"
                  color="primary"
                  textClass={"font-10"}
                />
                <Button
                  fullWidth
                  component="a"
                  sx={{ textAlign: "center", mb: "10px" }}
                  label={contactSupport}
                  onClick={handleContactSupport}
                  variant="outlined"
                  color="primary"
                  textClass={"font-10"}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box className="auth-wrapper-content">
            <Box className="auth-wrapper-body">
              <Text className="default-text" size={22} font="bold">
                {twoStepVerification}
              </Text>
              <Text className="grey-text mt-8" size={14}>
                {twoFactorAuthenticationSubHeader}
              </Text>
              <PhoneNumberInput
                onOpen={handleDynamicWidthForMenu}
                onClose={() => {
                  document.body.style.overflow = "auto";
                }}
                name="mobileNo"
                options={countries}
                error={touched?.mobileNo && Boolean(errors?.mobileNo)}
                selectValue={values?.mobileDialCode}
                MenuProps={{
                  id: "login-mfa-select-calling-code",
                  disableScrollLock: true,
                }}
                countryIsoCode={countryISOCode}
                inputValue={values.mobileNo}
                label={mobile}
                showLabel={true}
                onChangeSelect={(value) => {
                  setFieldValue(
                    "mobileDialCode",
                    typeof value === "object" ? value.calling_code : value
                  );
                }}
                onBlur={() => setTouched({ ...touched, mobileNo: true })}
                onChange={(value) => {
                  setTouched({ ...touched, mobileNo: false });
                  setFieldValue("mobileNo", value);
                }}
                boxClassName="mt-24"
              />
              <Text
                variant="subtitle1"
                font="regular"
                size={14}
                className="grey-text mt-8"
              >
                {carrierRateMsg}
              </Text>
              <Box textAlign="center" marginTop={2}>
                <Text
                  variant="subtitle1"
                  font="bold"
                  size={16}
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    setOpenSelectMethodModal(true);
                  }}
                  className="blue-text mt-8"
                >
                  {useAnotherMethod}
                </Text>
              </Box>
            </Box>
            {isFromOptions && authOptionsFooterDivider}
            <Box className={!isFromOptions ? "footer" : ""} left={0}>
              <Button
                fullWidth
                sx={!isFromOptions && { m: "14px 20px" }}
                variant="contained"
                label={sendCode}
                onClick={handleSubmit}
                disabled={isBtnDisabled}
                {...(!isFromOptions && { textClass: "font-14" })}
              />
            </Box>
          </Box>
        )}
        <SelectAuthenticationMethodDrawer
          openModal={openSelectMethodModal}
          setOpenModal={setOpenSelectMethodModal}
          mfaConfigurations={[{ mfa_type: "google_authenticator" }]}
          onChangeMethod={() => {
            setSelectedMethod("google_authenticator");
            !gAuthSetup && getGAuthConfigurations();
            setOpenSelectMethodModal(false);
          }}
          isFromBrowser={isFromOptions}
        />
      </CommonAuthHeader>
    </>
  );
};

export default Setup2FA;
